import { logger } from "@/utils/logger";

import React, { useRef } from 'react';
import { Card } from '@/components/ui/card';
import FormFramework, { FormFrameworkRef } from '../FormFramework';
import NewConversationDialog from '../NewConversationDialog';
import TradeBanner from '../forms/TradeBanner';
import ChatHeader from './ChatHeader';
import ChatMessages from './ChatMessages';
import ChatInput from './ChatInput';
import { useTradeEstimate } from '@/hooks/useTradeEstimate';
import { useFormContext } from '@/contexts/FormContext';
import { useFormAutoCollapse } from '@/hooks/useFormAutoCollapse';
import { FormAutoCollapseProvider, useFormAutoCollapseControl } from '@/contexts/FormAutoCollapseContext';
import { useCertificationStyles } from '@/hooks/useCertificationStyles';
import { DealerManager } from '@/utils/dealerManager';
import { useChatMessages } from '@/hooks/useChatMessages';
import { useLanguage } from '@/contexts/LanguageContext';

interface ChatLayoutDesktopProps {
  // Chat state
  messages: any[];
  isLoading: boolean;
  isSearching?: boolean;
  isRestoring?: boolean;
  chatId: string;
  sessionId: string;
  dealerId: string;
  waitingVideoData?: {
    Waiting_Youtube_Video_url: string;
    Waiting_Message: string;
  } | null;
  // Note: Message classification is now handled server-side
  streamingIntro?: {
    displayedIntro: string;
    isTyping: boolean;
    isComplete: boolean;
    isStreaming: boolean;
    isEnabled: boolean;
  };
  
  // Form state
  activeForm: any;
  isOfferHighlighted: boolean;
  formData: any;
  updateFormData: (data: any) => void;
  closeForm: (force?: boolean) => void;
  
  // Input state
  input: string;
  setInput: (input: string) => void;
  
  // Dialog state
  showNewConversationDialog: boolean;
  setShowNewConversationDialog: (show: boolean) => void;
  
  // Handlers
  onNewConversation: () => void;
  onCopyConversation: (messagesContainerRef?: React.RefObject<HTMLElement>) => void;
  onCollapse: () => void;
  onFormSubmit: (data: any) => void;
  onSendMessage: (message?: string) => void;
  onConfirmNewConversation: () => void;
  onRetryLastRequest?: () => Promise<void>;
}

const ChatLayoutDesktop: React.FC<ChatLayoutDesktopProps> = ({
  messages,
  isLoading,
  isSearching = false,
  isRestoring = false,
  chatId,
  sessionId,
  dealerId,
  waitingVideoData,
  streamingIntro,
  activeForm,
  isOfferHighlighted,
  formData,
  updateFormData,
  closeForm,
  input,
  setInput,
  showNewConversationDialog,
  setShowNewConversationDialog,
  onNewConversation,
  onCopyConversation,
  onCollapse,
  onFormSubmit,
  onSendMessage,
  onConfirmNewConversation,
  onRetryLastRequest,
}) => {
  const {
    isTradeEstimateEnabled,
    isTradeEstimateVisible,
    hideTradeEstimate,
    showTradeEstimate,
  } = useTradeEstimate();

  // Hide trade estimate when other forms are active
  React.useEffect(() => {
    if (activeForm) {
      hideTradeEstimate();
    } else if (!activeForm && isTradeEstimateEnabled) {
      showTradeEstimate();
    }
  }, [activeForm, hideTradeEstimate, showTradeEstimate, isTradeEstimateEnabled]);

  const { setFormRef, openingHours } = useFormContext();
  const isOnHomePage = true; // Widget is always treated as "home page"
  const formRef = useRef<FormFrameworkRef>(null);
  const chatScrollRef = useRef<HTMLDivElement>(null);
  const [isFormCollapsed, setIsFormCollapsed] = React.useState(false);
  const { styles } = useCertificationStyles();
  const { addAIMessage } = useChatMessages();
  const { t } = useLanguage();

  // Handler for form expansion from prompt menu
  const handleFormExpand = React.useCallback((formType: string) => {
    logger.log('🔧 Desktop Layout: Handling form expand for:', formType);
    // Clear input
    setInput('');
    
    // Add AI message for appointment forms
    if (formType === 'Appointment' || formType === 'Service') {
      const message = formType === 'Appointment' 
        ? t('chat.appointment.sales.message')
        : t('chat.appointment.service.message');
      
      setTimeout(async () => {
        await addAIMessage(message);
      }, 200);
    }
    
    // DO NOT expand form immediately - let useFormAutoCollapse handle it
    // Form will start collapsed and expand when user scrolls to bottom
    // This allows users to read the AI response before seeing the form
    
  }, [setInput, addAIMessage, t]);

  // Close all forms when OpenAI is loading (except Service/Appointment which should persist)
  //
  // The callbacks are held in refs rather than listed as dependencies: `closeForm`
  // comes from FormContext and `hideTradeEstimate` from useTradeEstimate, and both
  // change identity whenever this component re-renders. Listing them re-ran this
  // effect after every commit, and because `closeForm()` writes a brand-new state
  // object every time (see useStateManager.updateState) that produced an unbounded
  // render→effect→render loop that pegged the CPU for the whole request.
  // Guarding on `activeForm` makes the effect idempotent so it settles after one pass.
  const closeFormRef = React.useRef(closeForm);
  closeFormRef.current = closeForm;
  const hideTradeEstimateRef = React.useRef(hideTradeEstimate);
  hideTradeEstimateRef.current = hideTradeEstimate;

  React.useEffect(() => {
    if (!isLoading) return;
    if (activeForm !== 'Service' && activeForm !== 'Appointment') {
      if (activeForm) closeFormRef.current();
      hideTradeEstimateRef.current();
    }
  }, [isLoading, activeForm]);

  // Enable/disable form auto-collapse
  const AUTO_COLLAPSE_ENABLED = true;
  
  // Get auto-collapse control
  const { isTemporaryDisabled } = useFormAutoCollapseControl();

  // Always call useFormAutoCollapse to avoid hooks count mismatch
  useFormAutoCollapse({
    enabled: AUTO_COLLAPSE_ENABLED && !!activeForm,
    formRef,
    scrollAreaRef: chatScrollRef,
    temporaryDisabled: isTemporaryDisabled
  });

  // Set the form ref in context so VehicleCard can access it
  React.useEffect(() => {
    setFormRef(formRef);
  }, [setFormRef]);

  // Collapse form when streaming starts
  React.useEffect(() => {
    if (streamingIntro?.isStreaming && formRef.current) {
      logger.log('🔧 Desktop Layout: Collapsing form due to streaming start');
      formRef.current.collapse();
    }
  }, [streamingIntro?.isStreaming]);
  
  // Debug logging for activeForm
  React.useEffect(() => {
    logger.log('🔍 ChatLayoutDesktop: activeForm changed to:', activeForm);
  }, [activeForm]);

  const handleCollapseForm = () => {
    if (formRef.current) {
      formRef.current.collapse();
    }
  };

  return (
    <>
      <div className="fixed bottom-6 right-6 z-20 animate-scale-in">
        <Card 
          className="d2cbot-chat-container w-96 shadow-2xl bg-white/95 backdrop-blur-sm d2cbot-bot_border-0 flex flex-col h-[600px] max-h-[calc(100vh-48px)] relative p-0 m-0"
          style={{ 
            overflow: 'visible',
            borderRadius: `${styles.corner_radius}px`
          }}
        >
          
          <ChatHeader
            onNewConversation={onNewConversation}
            onCopyConversation={onCopyConversation}
            onCollapse={onCollapse}
            onSendMessage={onSendMessage}
            messagesContainerRef={chatScrollRef}
          />

          {/* Trade Estimate Form - appears right under header when enabled */}
          {isTradeEstimateEnabled && isTradeEstimateVisible && DealerManager.isTradeBannerEnabled() && (
            <TradeBanner
              onClose={hideTradeEstimate}
              onSubmit={onFormSubmit}
              activeForm={activeForm}
              isOnHomePage={isOnHomePage}
              isFormCollapsed={isFormCollapsed}
            />
          )}

          <ChatMessages
            ref={chatScrollRef}
            messages={messages}
            isLoading={isLoading}
            isSearching={isSearching}
            isRestoring={isRestoring}
            sessionId={sessionId}
            onSendMessage={onSendMessage}
            inputValue={input}
            setInputValue={setInput}
            onFormExpand={handleFormExpand}
            waitingVideoData={waitingVideoData}
            onRetryLastRequest={onRetryLastRequest}
            onCopyConversation={() => onCopyConversation(chatScrollRef)}
            streamingIntro={streamingIntro}
          />

          {/* Form Framework - pinned between messages and input */}
          {/* Forms start collapsed and expand when user scrolls to bottom of messages */}
          {activeForm && (
          <FormFramework
            ref={formRef}
            formType={activeForm}
            formData={formData}
            onUpdateData={updateFormData}
            onClose={() => closeForm(true)}
            onSubmit={onFormSubmit}
            isOfferHighlighted={isOfferHighlighted}
            onCollapse={setIsFormCollapsed}
            openingHours={openingHours}
            initialCollapsed={true}
          />
          )}

          <ChatInput
            inputValue={input}
            setInputValue={setInput}
            onSendMessage={onSendMessage}
            onCollapseForm={handleCollapseForm}
            disabled={isLoading}
            isWelcomeScreen={messages.length === 0}
            sessionId={sessionId}
            isFormCollapsed={isFormCollapsed}
            waitingVideoData={waitingVideoData}
            isLoading={isLoading}
          />
          
          {/* Footer centered in the space between input and bottom */}
          <div className="flex items-center justify-center py-[5px]">
          <span className="text-[11px] text-gray-400">
            {t('footer.disclaimer')}
          </span>
          </div>
        </Card>
      </div>

      <NewConversationDialog
        open={showNewConversationDialog}
        onOpenChange={setShowNewConversationDialog}
        onConfirm={onConfirmNewConversation}
      />
    </>
  );
};

const ChatLayoutDesktopWithProvider: React.FC<ChatLayoutDesktopProps> = (props) => {
  return (
    <FormAutoCollapseProvider>
      <ChatLayoutDesktop {...props} />
    </FormAutoCollapseProvider>
  );
};

export default ChatLayoutDesktopWithProvider;
